import { useContext, useEffect, useId, useRef, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  ContentCard,
  Drawer,
  Field,
  Radio,
  ResponsiveFlex,
  Row,
  Tooltip,
} from "@narmi/design_system";
import Payee from "byzantine/src/Payee";
import type { NewPayeeProps } from "byzantine/src/Payee";
import Address from "byzantine/src/Address";
import {
  extractIgnoredWarnings,
  getWarningMessage,
} from "cerulean/src/FormReviewWarningsContext/FormReviewWarningsContext";
import { STATES } from "cerulean/src/StateSelector";
import {
  useLoadingContext,
  useNotificationContext,
  useSetChatVisibility,
  validateEmail,
} from "cerulean";
import { useTriggerGuideForceReset } from "../../src/modules/guides/hooks";
import UserFeaturesContext from "../contexts/UserFeaturesContext";
import azulUtils from "../../utils";
import ViewPayee from "./ViewPayee";

// like the recipient drawer, one drawer adds a payee or, in view mode, shows
// an existing payee's details
type PayeeDrawerProps = {
  open: boolean;
  handleClose: () => void;
} & (
  | { mode?: "add"; updatePayees: (payee: Payee) => void }
  | { mode: "view"; payee: Payee; onDelete: () => void }
);

type PayeeFormData = {
  name: string;
  description: string;
  account_number: string;
  email: string;
  phone: string;
  passphrase: string;
  street_address: string;
  street_address_2: string;
  city: string;
  region_code: string;
  postal_code: string;
  country_code: string;
};

type PayeeField = keyof PayeeFormData;
type PayeeType = (typeof Payee.TYPE)[keyof typeof Payee.TYPE];
// iPay's invalid address warning (ADDRESS_INVALID_CODE in
// integrations/bill_pay/ipay/constants.py)
const ADDRESS_INVALID_WARNING_CODE = "994051";

type Warning = { code?: string; description?: string };

const EMPTY_FORM: PayeeFormData = {
  name: "",
  description: "",
  account_number: "",
  email: "",
  phone: "",
  passphrase: "",
  street_address: "",
  street_address_2: "",
  city: "",
  region_code: "",
  postal_code: "",
  country_code: "",
};

const BUSINESS_REQUIRED_FIELDS: PayeeField[] = [
  "name",
  "description",
  "account_number",
  "phone",
  "street_address",
  "city",
  "region_code",
  "postal_code",
  "country_code",
];

const PERSON_REQUIRED_FIELDS: PayeeField[] = [
  "name",
  "description",
  "email",
  "phone",
  "passphrase",
];

// ZIP or ZIP+4, inserting the hyphen like ZipCodeTextInput did so that a
// partial extension ("10001-2") fails validateAddress instead of submitting
// as a six-digit ZIP
const formatZipCode = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  return digits.length > 5
    ? `${digits.slice(0, 5)}-${digits.slice(5)}`
    : digits;
};

// the API expects +1 and the 10 digits of a US phone number
const toApiPhone = (phone: string) => `+1${phone.replace(/\D/g, "")}`;

// "PAYEE TYPE", "PAYEE DETAILS" and "SECRET PHRASE" headers from the design
const SectionHeader = ({ children }: { children: string }) => (
  <div
    className="fontSize--xs fontWeight--bold fontColor--secondary margin--bottom--xs"
    style={{ textTransform: "uppercase" }}
  >
    {children}
  </div>
);

const PayeeDrawer = (props: PayeeDrawerProps) => {
  const { handleClose, open } = props;
  const updatePayees = props.mode === "view" ? undefined : props.updatePayees;
  const { l10n } = useLocalization();
  const { isLoading, setIsLoading } = useLoadingContext();
  const { sendNotification, clearNotifications } = useNotificationContext();
  const drawerId = useId();
  useSetChatVisibility(drawerId, !open);
  const { disable_billpay_create_person_payee: disablePersonalPayee } =
    useContext(UserFeaturesContext);
  const triggerGuideForceReset = useTriggerGuideForceReset();
  const [payeeType, setPayeeType] = useState<PayeeType>(Payee.TYPE.BUSINESS);
  const [formData, setFormData] = useState<PayeeFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<PayeeField, string>>>({});
  const [isPassphraseVisible, setIsPassphraseVisible] = useState(false);
  // warnings the user has seen and can override by submitting again
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const warningRef = useRef<HTMLDivElement>(null);
  const isBusiness = payeeType === Payee.TYPE.BUSINESS;
  // the invalid address warning uses the design's wording; other warnings
  // keep the API's description
  const warningMessage = getWarningMessage(
    warnings.map((warning) =>
      warning.code === ADDRESS_INVALID_WARNING_CODE
        ? { description: l10n.getString("add-payee-unverified-address") }
        : warning
    )
  );

  // reopening keeps the draft but drops validation errors and any override
  useEffect(() => {
    if (!open) {
      setErrors({});
      setWarnings([]);
    }
  }, [open]);

  useEffect(() => {
    warningRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [warningMessage]);

  const updateField = (field: PayeeField, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const getErrors = (field: PayeeField) =>
    errors[field] ? [errors[field] as string] : undefined;

  const validate = () => {
    const requiredFields = isBusiness
      ? BUSINESS_REQUIRED_FIELDS
      : PERSON_REQUIRED_FIELDS;
    const nextErrors: Partial<Record<PayeeField, string>> = {};
    requiredFields.forEach((field) => {
      if (!formData[field].trim()) {
        nextErrors[field] = l10n.getString("error-required");
      }
    });
    if (formData.phone && formData.phone.replace(/\D/g, "").length !== 10) {
      nextErrors.phone = l10n.getString("add-payee-phone-invalid");
    }
    if (!isBusiness && formData.email) {
      const emailError = validateEmail(
        formData.email,
        false,
        l10n.getString("add-payee-email-invalid")
      );
      if (emailError) nextErrors.email = emailError;
    }
    if (isBusiness && formData.postal_code) {
      const { postal_code: postalCodeError } = azulUtils.validateAddress(
        formData as unknown as API.Address
      );
      if (postalCodeError) nextErrors.postal_code = postalCodeError;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // matches ContextForm's error handling: arrays (e.g. Django's
  // non_field_errors) are joined into sentences ending with a period
  const showSubmitError = (error: unknown) => {
    let message = "";
    if (typeof error === "string") {
      message = error;
    } else if (Array.isArray(error)) {
      message = error.filter((item) => typeof item === "string").join(". ");
    } else if (
      error &&
      typeof error === "object" &&
      !(error instanceof Error) &&
      !("response" in error)
    ) {
      // DRF-style errors: "__all__" for the whole form, field names for inputs
      const { __all__: formError, ...fieldErrors } = error as Record<
        string,
        string | string[]
      >;
      if (formError) {
        message = Array.isArray(formError) ? formError.join(". ") : formError;
      }
      setErrors((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(fieldErrors)
            .filter(([field]) => field in EMPTY_FORM)
            .map(([field, value]) => [
              field,
              Array.isArray(value) ? value.join(" ") : value,
            ])
        ),
      }));
    }
    if (/[a-zA-Z0-9]$/.test(message)) {
      message += ".";
    }
    sendNotification({
      type: "negative",
      text: message || l10n.getString("add-payee-error-generic"),
    });
  };

  const onSubmit = () => {
    if (isLoading) return;
    // like ContextForm, drop the last attempt's error so it doesn't linger
    // next to this attempt's result (negative notifications don't auto-dismiss)
    clearNotifications();
    if (!validate()) return;
    const address = {
      street_address: formData.street_address,
      street_address_2: formData.street_address_2,
      city: formData.city,
      region_code: formData.region_code,
      postal_code: formData.postal_code,
      country_code: formData.country_code,
    };
    // only business payees have an address
    if (isBusiness) {
      const payeeAddress: Address = new Address(address);
      try {
        payeeAddress.validate();
      } catch {
        showSubmitError(l10n.getString("add-payee-address-required"));
        return;
      }
    }
    const newPayee = new Payee({
      type: payeeType,
      name: formData.name,
      description: formData.description,
      phone: toApiPhone(formData.phone),
      email: formData.email,
      passphrase: formData.passphrase,
      address,
      account_number: formData.account_number,
      ignore_warnings: warnings.length ? warnings : undefined,
    } as NewPayeeProps);
    setIsLoading(true);
    setWarnings([]);
    newPayee
      .submit()
      .then((response: { payee?: Parameters<typeof Payee.deserialize>[0] }) => {
        if (response.payee) {
          updatePayees?.(Payee.deserialize(response.payee));
          triggerGuideForceReset();
          handleClose();
          // a successful add starts the next one from a blank business payee
          setFormData(EMPTY_FORM);
          setPayeeType(Payee.TYPE.BUSINESS);
          setIsPassphraseVisible(false);
        }
      })
      .catch((error: unknown) => {
        const nextWarnings = extractIgnoredWarnings(error) as Warning[];
        if (nextWarnings.length) {
          setWarnings(nextWarnings);
          return;
        }
        showSubmitError(error);
      })
      .finally(() => setIsLoading(false));
  };

  const payeeTypeOptions = [
    { label: l10n.getString("label-business"), value: Payee.TYPE.BUSINESS },
    ...(disablePersonalPayee
      ? []
      : [
          {
            label: l10n.getString("label-personal"),
            value: Payee.TYPE.PERSON,
          },
        ]),
  ];

  return (
    <Drawer
      isOpen={open}
      onUserDismiss={handleClose}
      position="right"
      depth="633px"
      paddingSize="xl"
      showControls={false}
      testId={props.mode === "view" ? "view-payee-drawer" : "add-payee-drawer"}
      footer={
        props.mode === "view" ? (
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="tertiary"
                testId="close-view-payee-drawer"
                onClick={handleClose}
                label={l10n.getString("button-close")}
              />
            </Row.Item>
          </Row>
        ) : (
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                testId="drawer-cancel"
                onClick={handleClose}
                disabled={isLoading}
                label={l10n.getString("button-cancel")}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                kind="primary"
                testId="drawer-save"
                onClick={onSubmit}
                disabled={isLoading}
                label={
                  warnings.length
                    ? l10n.getString("add-payee-override-submit")
                    : l10n.getString("add-payee-submit")
                }
              />
            </Row.Item>
          </Row>
        )
      }
    >
      {props.mode === "view" ? (
        <ViewPayee
          payee={props.payee}
          isOpen={open}
          onDelete={props.onDelete}
        />
      ) : (
        <ResponsiveFlex gapSize="xl">
          <h2>{l10n.getString("add-payee-title")}</h2>
          <div>
            <SectionHeader>{l10n.getString("add-payee-type")}</SectionHeader>
            <Row gapSize="m">
              {payeeTypeOptions.map(({ label, value }) => (
                <Row.Item key={value}>
                  <Radio
                    kind="card"
                    name="payeeType"
                    value={value}
                    checked={payeeType === value}
                    onCheck={() => {
                      setPayeeType(value);
                      setErrors({});
                    }}
                  >
                    {label}
                  </Radio>
                </Row.Item>
              ))}
            </Row>
          </div>
          <div>
            <SectionHeader>{l10n.getString("add-payee-details")}</SectionHeader>
            <ContentCard kind="bordered" paddingSize="l">
              <ResponsiveFlex gapSize="m">
                {warningMessage ? (
                  <div ref={warningRef}>
                    <Alert kind="warn" isActive>
                      {warningMessage}
                    </Alert>
                  </div>
                ) : null}
                {isBusiness ? (
                  <div className="fontSize--xs fontColor--secondary">
                    {l10n.getString("add-payee-details-instructions")}
                  </div>
                ) : null}
                <Field.Text
                  label={l10n.getString("label-name")}
                  value={formData.name}
                  onChange={(value) => updateField("name", value)}
                  errors={getErrors("name")}
                  required
                />
                {isBusiness ? (
                  <>
                    <Field.Text
                      label={l10n.getString("add-payee-description-label")}
                      placeholder={l10n.getString(
                        "add-payee-description-placeholder"
                      )}
                      value={formData.description}
                      onChange={(value) => updateField("description", value)}
                      errors={getErrors("description")}
                      required
                    />
                    <Field.Text
                      label={l10n.getString("add-payee-account-number")}
                      value={formData.account_number}
                      onChange={(value) => updateField("account_number", value)}
                      errors={getErrors("account_number")}
                      endContent={
                        <Tooltip
                          text={l10n.getString("add-payee-account-number-help")}
                        >
                          <span className="narmi-icon-info" />
                        </Tooltip>
                      }
                      required
                    />
                  </>
                ) : (
                  <>
                    <Field.Text
                      label={l10n.getString("textinput-label-nickname")}
                      value={formData.description}
                      onChange={(value) => updateField("description", value)}
                      errors={getErrors("description")}
                      required
                    />
                    <Field.Text
                      label={l10n.getString("label-email")}
                      // not type="email": Field.Text's input masking moves the
                      // cursor, which email inputs don't allow
                      inputMode="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(value) => updateField("email", value)}
                      errors={getErrors("email")}
                      required
                    />
                  </>
                )}
                <Field.Text
                  label={l10n.getString("label-phone-number")}
                  placeholder={l10n.getString("add-payee-phone-placeholder")}
                  type="tel"
                  mask={Field.MASKS.Phone}
                  value={formData.phone}
                  onChange={(value) => updateField("phone", value)}
                  errors={getErrors("phone")}
                  required
                />
                {isBusiness ? (
                  <>
                    <Row gapSize="m" alignItems="top">
                      <Row.Item>
                        <Field.Text
                          label={l10n.getString("label-street-address")}
                          value={formData.street_address}
                          onChange={(value) =>
                            updateField("street_address", value)
                          }
                          errors={getErrors("street_address")}
                          autoComplete="address-line1"
                          required
                        />
                      </Row.Item>
                      <Row.Item shrink>
                        <div style={{ width: "140px" }}>
                          <Field.Text
                            label={l10n.getString("add-payee-apartment-label")}
                            value={formData.street_address_2}
                            onChange={(value) =>
                              updateField("street_address_2", value)
                            }
                            autoComplete="address-line2"
                          />
                        </div>
                      </Row.Item>
                    </Row>
                    <Field.Text
                      label={l10n.getString("label-city")}
                      value={formData.city}
                      onChange={(value) => updateField("city", value)}
                      errors={getErrors("city")}
                      autoComplete="address-level2"
                      required
                    />
                    <Row gapSize="m" alignItems="top">
                      <Row.Item>
                        <Field.Select
                          label={l10n.getString("label-state")}
                          value={formData.region_code}
                          onChange={(value) =>
                            updateField("region_code", value)
                          }
                          errors={getErrors("region_code")}
                        >
                          {STATES.map(({ code, name }) => (
                            <Field.Select.Item key={code} value={code}>
                              {name}
                            </Field.Select.Item>
                          ))}
                        </Field.Select>
                      </Row.Item>
                      <Row.Item shrink>
                        <div style={{ width: "140px" }}>
                          <Field.Text
                            label={l10n.getString("label-postal")}
                            value={formData.postal_code}
                            onChange={(value) =>
                              updateField("postal_code", formatZipCode(value))
                            }
                            errors={getErrors("postal_code")}
                            inputMode="numeric"
                            autoComplete="postal-code"
                            required
                          />
                        </div>
                      </Row.Item>
                    </Row>
                    <Field.Select
                      label={l10n.getString("add-payee-country")}
                      value={formData.country_code}
                      onChange={(value) => updateField("country_code", value)}
                      errors={getErrors("country_code")}
                    >
                      {Payee.GEO_COUNTRIES.map(({ code, name }) => (
                        <Field.Select.Item key={code} value={code}>
                          {name}
                        </Field.Select.Item>
                      ))}
                    </Field.Select>
                  </>
                ) : null}
              </ResponsiveFlex>
            </ContentCard>
          </div>
          {isBusiness ? null : (
            <div>
              <SectionHeader>
                {l10n.getString("add-payee-secret-phrase")}
              </SectionHeader>
              <ContentCard kind="bordered" paddingSize="l">
                <ResponsiveFlex gapSize="m">
                  <div className="fontSize--xs fontColor--secondary">
                    {l10n.getString("add-payee-secret-phrase-help")}
                  </div>
                  <Field.Text
                    label={l10n.getString("add-payee-secret-phrase")}
                    type={isPassphraseVisible ? "text" : "password"}
                    value={formData.passphrase}
                    onChange={(value) => updateField("passphrase", value)}
                    errors={getErrors("passphrase")}
                    autoComplete="off"
                    endContent={
                      <Button
                        kind="plain"
                        size="xs"
                        type="button"
                        onClick={() => setIsPassphraseVisible((prev) => !prev)}
                        label={
                          isPassphraseVisible
                            ? l10n.getString("input-password-hide")
                            : l10n.getString("input-password-show")
                        }
                      />
                    }
                    required
                  />
                </ResponsiveFlex>
              </ContentCard>
            </div>
          )}
        </ResponsiveFlex>
      )}
    </Drawer>
  );
};

export default PayeeDrawer;
